import { useState, useEffect, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { useExtensionsQuery } from '@/hooks/useDatabase'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'

interface Extension {
  name: string
  version?: string
  schema?: string
  owner?: string
  relocatable?: boolean
  description?: string
}

// Type guard to check if an object is an Extension
function isExtension(obj: unknown): obj is Extension {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'name' in obj &&
    typeof (obj as { name: unknown }).name === 'string'
  )
}


interface ExtensionDetailsViewProps {
  connection: DatabaseConnection
  database: string
  extensionName: string
  schema?: string
}

export function ExtensionDetailsView({ connection, database, extensionName }: ExtensionDetailsViewProps) {
  const [isUninstalling, setIsUninstalling] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [updateVersion, setUpdateVersion] = useState('')
  const [showConfirmUninstall, setShowConfirmUninstall] = useState(false)
  const [showConfirmUninstallCascade, setShowConfirmUninstallCascade] = useState(false)
  const { showToast } = useToast()

  const { data: extensionsResponse, isLoading, error } = useExtensionsQuery(
    connection.id,
    database,
    true, // isPostgreSQL
    { enabled: true }
  )

  // Safely extract extensions array from the response
  const extensions: Extension[] = Array.isArray(extensionsResponse)
    ? extensionsResponse.filter(isExtension)
    : []

  const extension = extensions.find((ext: Extension) => ext.name === extensionName) || null

  useEffect(() => {
    if (error) {
      console.error('Failed to fetch extensions:', error)
      showToast('error', 'Failed to load extension details')
    }
  }, [error, showToast])

  const handleUninstallClick = (cascade = false) => {
    if (cascade) {
      setShowConfirmUninstallCascade(true)
    } else {
      setShowConfirmUninstall(true)
    }
  }

  const handleConfirmUninstall = useCallback(async (cascade = false) => {
    try {
      setIsUninstalling(true)
      setShowConfirmUninstall(false)
      setShowConfirmUninstallCascade(false)
      
      await AgentProxyService.dropExtension(
        connection.id,
        database,
        extensionName,
        cascade
      )
      
      showToast('success', `Extension "${extensionName}" uninstalled successfully`)
      // TODO: Navigate back or refresh the extension list
    } catch (error) {
      console.error('Failed to uninstall extension:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to uninstall extension'
      showToast('error', errorMessage)
    } finally {
      setIsUninstalling(false)
    }
  }, [connection.id, database, extensionName, showToast])

  const handleCloseConfirmUninstall = useCallback(() => {
    setShowConfirmUninstall(false)
  }, [])

  const handleCloseConfirmUninstallCascade = useCallback(() => {
    setShowConfirmUninstallCascade(false)
  }, [])

  const handleConfirmUninstallWrapper = useCallback(() => {
    void handleConfirmUninstall(false)
  }, [handleConfirmUninstall])

  const handleConfirmUninstallCascadeWrapper = useCallback(() => {
    void handleConfirmUninstall(true)
  }, [handleConfirmUninstall])

  const handleUpdate = async () => {
    if (!updateVersion.trim()) {
      showToast('error', 'Please enter a version number')
      return
    }

    try {
      setIsUpdating(true)
      
      await AgentProxyService.alterExtension(
        connection.id,
        database,
        extensionName,
        {
          newVersion: updateVersion.trim()
        }
      )
      
      showToast('success', `Extension "${extensionName}" updated to version ${updateVersion}`)
      setShowUpdateModal(false)
      setUpdateVersion('')
      // TODO: Refresh extension details
    } catch (error: unknown) {
      console.error('Failed to update extension:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to update extension'
      showToast('error', errorMessage)
    } finally {
      setIsUpdating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!extension) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Extension Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The extension "{extensionName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="text-3xl mr-4">🧩</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {extension.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  PostgreSQL Extension • Version {extension.version || 'Unknown'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button type="button"
                onClick={() => { setShowUpdateModal(true) }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Update Version
              </button>
              
              <button type="button"
                onClick={() => { handleUninstallClick(false) }}
                disabled={isUninstalling}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUninstalling ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uninstalling...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Uninstall
                  </>
                )}
              </button>
              
              <button type="button"
                onClick={() => { handleUninstallClick(true) }}
                disabled={isUninstalling}
                className="flex items-center px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Uninstall extension and all dependent objects"
              >
                Uninstall CASCADE
              </button>
            </div>
          </div>
        </div>

        {/* Extension Properties */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Extension Properties
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Name
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {extension.name}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Version
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {extension.version || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Schema
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {extension.schema || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Owner
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {extension.owner || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Relocatable
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {extension.relocatable !== undefined ? (extension.relocatable ? 'Yes' : 'No') : 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Status
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Installed
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        {extension.description && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Description
            </h2>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {extension.description}
            </p>
          </div>
        )}

        {/* Common Extensions Information */}
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-teal-900 dark:text-teal-200 mb-3">
            About PostgreSQL Extensions
          </h3>
          <div className="space-y-2 text-sm text-teal-800 dark:text-teal-300">
            <p>• Extensions add functionality to PostgreSQL databases</p>
            <p>• Popular extensions include: pgcrypto, uuid-ossp, hstore, PostGIS, pg_stat_statements</p>
            <p>• Extensions are installed per-database and can be schema-specific</p>
            <p>• Some extensions require superuser privileges to install</p>
            <p>• Extensions can be updated to newer versions when available</p>
          </div>
        </div>

        {/* Popular Extensions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Popular PostgreSQL Extensions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">pgcrypto</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Cryptographic functions</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">uuid-ossp</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">UUID generation functions</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">hstore</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Key-value data type</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">PostGIS</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Spatial and geographic objects</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">pg_stat_statements</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Query execution statistics</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white">pg_trgm</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Trigram matching for text search</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Update Version Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Update Extension Version
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Version
              </label>
              <p className="text-gray-900 dark:text-white font-mono">
                {extension.version || 'Unknown'}
              </p>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Version
              </label>
              <input
                type="text"
                value={updateVersion}
                onChange={(e) => { setUpdateVersion(e.target.value) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter new version (e.g., 1.2)"
                autoFocus
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Enter the version number to update to. Leave empty for latest version.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button type="button"
                onClick={() => {
                  setShowUpdateModal(false)
                  setUpdateVersion('')
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button type="button"
                onClick={() => { void handleUpdate() }}
                disabled={isUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showConfirmUninstall}
        onClose={handleCloseConfirmUninstall}
        onConfirm={handleConfirmUninstallWrapper}
        title="Uninstall Extension"
        message={`Are you sure you want to uninstall the extension "${extensionName}"? This action cannot be undone.`}
        confirmText="Uninstall"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />

      <ConfirmationModal
        isOpen={showConfirmUninstallCascade}
        onClose={handleCloseConfirmUninstallCascade}
        onConfirm={handleConfirmUninstallCascadeWrapper}
        title="Uninstall Extension (CASCADE)"
        message={`Are you sure you want to uninstall the extension "${extensionName}" and all dependent objects? This action cannot be undone and may affect other database objects.`}
        confirmText="Uninstall CASCADE"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}
